import type { CustomerFieldBindingContract } from '@/contracts/customer-application-binding'
import { QuestionGroupModeSchema } from '@/contracts/question-groups'
import type { QuestionOutlineItem } from '@/contracts/question-groups'
import type { Form, FormStatus, LogicJump, PublicForm, Question } from '@/types/form'
import { LayoutQuestionTypeSchema, QuestionTypeSchema } from '@/types/form'
import { z } from 'zod'

export const ContractSourceMetaSchema = z.object({
  source: z.enum(['synthetic', 'database']),
  permissionScope: z.string().min(1),
  dataDate: z.string(),
  confidence: z.number().min(0).max(1),
})

export type ContractSourceMeta = z.infer<typeof ContractSourceMetaSchema>

export const FormListSortSchema = z.enum(['updated_desc', 'responses_desc', 'title_asc'])
export type FormListSort = z.infer<typeof FormListSortSchema>

export const FormViewModeSchema = z.enum(['grid', 'list'])
export type FormViewMode = z.infer<typeof FormViewModeSchema>

export const FormStatusFilterSchema = z.union([
  z.literal('all'),
  z.literal('draft'),
  z.literal('published'),
  z.literal('closed'),
  z.literal('archived'),
])
export type FormStatusFilter = z.infer<typeof FormStatusFilterSchema>

export const QuestionTypeCategorySchema = z.enum([
  'basic',
  'choice',
  'rating',
  'media',
  'layout',
  'advanced',
])
export type QuestionTypeCategory = z.infer<typeof QuestionTypeCategorySchema>

export const QuestionTypeMetaContractSchema = z.object({
  type: QuestionTypeSchema,
  zhName: z.string(),
  category: QuestionTypeCategorySchema,
  description: z.string(),
  answerable: z.boolean(),
  popular: z.boolean().optional(),
})
export type QuestionTypeMetaContract = z.infer<typeof QuestionTypeMetaContractSchema>

export const LayoutQuestionTypeContractSchema = LayoutQuestionTypeSchema
export type LayoutQuestionTypeContract = z.infer<typeof LayoutQuestionTypeContractSchema>

export const QuestionGroupUiContractSchema = z.object({
  id: z.string(),
  formId: z.string(),
  sourceGroupId: z.string().optional(),
  mode: QuestionGroupModeSchema.optional(),
  title: z.string(),
  description: z.string().optional(),
  orderIndex: z.number(),
  collapsed: z.boolean(),
  questionIds: z.array(z.string()),
  questions: z.array(z.custom<Question>()).optional(),
  logicJumps: z.array(z.custom<LogicJump>()).optional(),
  lockedReason: z.string().optional(),
  sourceMeta: ContractSourceMetaSchema.optional(),
})
export type QuestionGroupUiContract = z.infer<typeof QuestionGroupUiContractSchema>

export type QuestionUiContract = Question & {
  groupId?: string
  fieldCode?: string
  exportLabel?: string
  customerFieldBinding?: CustomerFieldBindingContract
}

export type FormUiContract = Form & {
  responseCount: number
  completionRate: number
  folderIds: string[]
  customerWorkflow?: 'standard' | 'customer_onboarding'
}

export type FormDetailContract = PublicForm & {
  groups: QuestionGroupUiContract[]
  outline: QuestionOutlineItem[]
  responseCount: number
  completionRate: number
  sourceMeta: ContractSourceMeta
}

export const ListFormsParamsSchema = z.object({
  workspaceId: z.string().optional(),
  keyword: z.string().optional(),
  status: FormStatusFilterSchema.default('all'),
  folderId: z.string().optional(),
  sort: FormListSortSchema.default('updated_desc'),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
})
export type ListFormsParams = z.input<typeof ListFormsParamsSchema>

export type NormalizedListFormsParams = z.output<typeof ListFormsParamsSchema>

export type ListFormsResult = {
  data: FormUiContract[]
  meta: ContractSourceMeta & {
    total: number
    page: number
    limit: number
  }
}

export const FormFolderContractSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  color: z.string(),
  orderIndex: z.number(),
  formIds: z.array(z.string()),
})
export type FormFolderContract = z.infer<typeof FormFolderContractSchema>

export type FormFolderListResult = {
  data: FormFolderContract[]
  meta: ContractSourceMeta
}

export type FormMutationPreview = {
  formId: string
  status: FormStatus
  nextUpdatedAt: string
  sourceMeta: ContractSourceMeta
}

export function isCustomerOnboardingForm(form: Pick<Form, 'title' | 'settings'>) {
  return (
    form.settings.customerWorkflow === 'customer_onboarding' ||
    form.settings.isCustomerIntake === true ||
    form.title.includes('客戶') ||
    form.title.includes('建檔')
  )
}

export function createSyntheticMeta(dataDate = '2026-05-20'): ContractSourceMeta {
  return {
    source: 'synthetic',
    permissionScope: 'mock-local',
    dataDate,
    confidence: 1,
  }
}

export function createDatabaseSourceMeta(
  dataDate = new Date().toISOString().slice(0, 10),
): ContractSourceMeta {
  return {
    source: 'database',
    permissionScope: 'database',
    dataDate,
    confidence: 1,
  }
}

export function createEmptyFormDetail(form: Form): FormDetailContract {
  return {
    ...form,
    questions: [],
    logicJumps: [] as LogicJump[],
    hiddenFields: {},
    groups: [],
    outline: [],
    responseCount: 0,
    completionRate: 0,
    sourceMeta: createSyntheticMeta(),
  }
}
